import { useCallback, useState } from 'react';
import { callAction } from '@/api/agency-action-api';
import { ActionData, Directives } from '@/schemas/actionDataSchema';
import { decodeSubmission } from '@/schemas/validation/ValidationSchemaGenerator';
import { RouteFn } from './use-flow';
import { reportError, reportMessage } from '@/services/error-reporting-service';

interface UseActionResult {
  isLoading: boolean;
  executeAction: (name: string, args: Record<string, unknown>, routeFn: RouteFn) => Promise<void>;
  submit: (name: string, submissionData: Record<string, unknown>, routeFn: RouteFn) => void;
  submitAsync: (name: string, submissionData: Record<string, unknown>, routeFn: RouteFn) => Promise<void>;
}

export function useAction(): UseActionResult {
  const [isLoading, setIsLoading] = useState(false);

  const handleError = useCallback((error: unknown) => {
    reportError({ error });
    setIsLoading(false);
  }, []);

  // routeFn() with no args triggers a same-scene refresh (re-fetches current step from server).
  // routeFn(entity_index, step_id) navigates to a different step.
  // This replaces the old pushState+popstate pattern and is scoped to the REFRESH/ROUTE directives,
  // so other flows (form submit, init) are unaffected.
  const runDirective = useCallback((routeFn: RouteFn, actionData: ActionData) => {
    if (actionData == null) {
      reportMessage({ message: 'Error executing action: No data returned' });
      return routeFn();
    }
    if ('message' in actionData) {
      throw new Error(actionData.message ?? undefined);
    }

    if (actionData.directive === Directives.REFRESH) {
      return routeFn();
    }

    if (actionData.directive === Directives.ROUTE) {
      return routeFn(actionData.routing.entity_index, actionData.routing.step_id);
    }
  }, []);

  // Execute an action with given name and arguments
  const executeAction = useCallback(
    async (name: string, args: Record<string, unknown> = {}, routeFn: RouteFn): Promise<void> => {
      try {
        setIsLoading(true);

        const data = await callAction(name, args);
        await runDirective(routeFn, data);
        setIsLoading(false);
      } catch (error) {
        handleError(error);
        throw error;
      }
    },
    [handleError, runDirective]
  );

  // Submit form data after decoding it
  // very similar to executeAction but specifically for form submissions
  const submitAsync = useCallback(
    async (name: string, submissionData: Record<string, unknown>, routeFn: RouteFn): Promise<void> => {
      try {
        setIsLoading(true);

        const data = await callAction(name, decodeSubmission(submissionData));

        void runDirective(routeFn, data);
        setIsLoading(false);
      } catch (error) {
        handleError(error);
      }
    },
    [handleError, runDirective]
  );

  const submit = useCallback(
    (name: string, submissionData: Record<string, unknown>, routeFn: RouteFn): void => {
      try {
        setIsLoading(true);

        return void callAction(name, decodeSubmission(submissionData)).then((data) => {
          void runDirective(routeFn, data);
          setIsLoading(false);
        });
      } catch (error) {
        handleError(error);
      }
    },
    [handleError, runDirective]
  );

  return { isLoading, executeAction, submit, submitAsync };
}
